/**
 * Thin, safe wrapper over the self-hosted Umami tracker.
 *
 * The Umami script is injected in index.html only when VITE_UMAMI_WEBSITE_ID is
 * configured (see the inline loader there). When it isn't — local dev without
 * analytics, or before the website-id is provisioned — `window.umami` is
 * undefined and every call here is a no-op. Nothing throws, and no network
 * request is made.
 *
 * Umami is cookieless and privacy-first: no consent banner is required.
 */

interface UmamiTracker {
  track: {
    (): void;
    (eventName: string, data?: Record<string, unknown>): void;
    (payload: Record<string, unknown>): void;
  };
}

declare global {
  interface Window {
    umami?: UmamiTracker;
  }
}

/** True when the Umami tracker has loaded and is ready. */
export function analyticsReady(): boolean {
  return typeof window !== "undefined" && typeof window.umami?.track === "function";
}

/**
 * Record a pageview for the given path. Umami auto-tracks the first load; this
 * is used for SPA client-side route changes, which it would otherwise miss.
 */
export function trackPageview(url: string): void {
  if (!analyticsReady()) return;
  // Callback-transform form (Umami v2): spread the default props (website id
  // etc.) and override url/title for this client-side navigation. document.title
  // is already updated by react-helmet-async before this runs on route change.
  const title = typeof document !== "undefined" ? document.title : undefined;
  window.umami!.track((props) => ({ ...(props as object), url, title }));
}

/** Record a named custom event (e.g. a conversion). Safe no-op if not ready. */
export function trackEvent(name: string, data?: Record<string, unknown>): void {
  if (!analyticsReady()) return;
  window.umami!.track(name, data);
}
